import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Loader2, CheckCircle, XCircle, FileText, Home, 
  Bed, Bath, Car, Ruler, MapPin
} from "lucide-react";
import { API } from "@/App";

export const SellerBrochureApprovalPage = () => {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [brochure, setBrochure] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [approval, setApproval] = useState(null);
  const [error, setError] = useState(null);
  const [amendments, setAmendments] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchBrochure();
  }, [token]);

  const fetchBrochure = async () => {
    try {
      const res = await axios.get(`${API}/documents/brochure/approval/${token}`);
      setBrochure(res.data.brochure);
      setCaseData(res.data.case);
      setApproval(res.data.approval);
      
      if (res.data.approval?.status !== "pending") {
        setSubmitted(true);
      }
    } catch (error) {
      setError(error.response?.data?.detail || "Invalid or expired approval link");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/documents/brochure/approval/${token}/respond`, {
        approved: true,
        amendments: ""
      });
      toast.success("Brochure approved! Thank you.");
      setSubmitted(true);
    } catch (error) {
      toast.error("Failed to submit approval");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestAmendments = async () => {
    if (!amendments.trim()) {
      toast.error("Please describe the amendments you'd like");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/documents/brochure/approval/${token}/respond`, {
        approved: false,
        amendments: amendments
      });
      toast.success("Amendment request submitted. We'll be in touch shortly.");
      setSubmitted(true);
    } catch (error) {
      toast.error("Failed to submit amendments");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Link Invalid</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">
              {approval?.status === "approved" 
                ? "Your brochure has been approved and will be published shortly."
                : "Your feedback has been received. We'll make the requested changes and send you an updated version."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-amber-600 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-2xl font-bold">Goodacres</h1>
          <p className="text-amber-100">Property Brochure Review</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Property Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              {brochure?.cover_image && (
                <img 
                  src={brochure.cover_image.startsWith('http') ? brochure.cover_image : `${API.replace('/api', '')}${brochure.cover_image}`}
                  alt="Property"
                  className="w-48 h-32 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">{brochure?.property_name || caseData?.address_line1}</h2>
                <p className="text-muted-foreground flex items-center gap-1 mb-3">
                  <MapPin className="w-4 h-4" />
                  {brochure?.location || caseData?.postcode}
                </p>
                <p className="text-3xl font-bold text-amber-600">
                  £{(brochure?.price || caseData?.asking_price || 0).toLocaleString()}
                </p>
                
                {/* Property Stats */}
                <div className="flex gap-6 mt-4 text-sm">
                  {brochure?.bedrooms && (
                    <span className="flex items-center gap-1">
                      <Bed className="w-4 h-4" /> {brochure.bedrooms} Beds
                    </span>
                  )}
                  {brochure?.bathrooms && (
                    <span className="flex items-center gap-1">
                      <Bath className="w-4 h-4" /> {brochure.bathrooms} Baths
                    </span>
                  )}
                  {brochure?.receptions && (
                    <span className="flex items-center gap-1">
                      <Home className="w-4 h-4" /> {brochure.receptions} Receptions
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Features */}
        {brochure?.key_features?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Key Features</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid grid-cols-2 gap-2">
                {brochure.key_features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Description */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Property Description</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              {brochure?.description_page2 && (
                <div className="mb-4">
                  <h4 className="font-medium text-slate-700 mb-2">Summary</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{brochure.description_page2}</p>
                </div>
              )}
              {brochure?.description_page3 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Full Description</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{brochure.description_page3}</p>
                </div>
              )}
              {brochure?.full_description && !brochure?.description_page3 && (
                <p className="text-slate-600 whitespace-pre-wrap">{brochure.full_description}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        {brochure?.photos?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Property Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {brochure.photos.map((photo, idx) => (
                  <img 
                    key={idx}
                    src={photo.startsWith('http') ? photo : `${API.replace('/api', '')}${photo}`}
                    alt={`Property photo ${idx + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Floorplan */}
        {brochure?.floorplan_image && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Floorplan</CardTitle>
            </CardHeader>
            <CardContent>
              <img 
                src={brochure.floorplan_image.startsWith('http') ? brochure.floorplan_image : `${API.replace('/api', '')}${brochure.floorplan_image}`}
                alt="Floorplan"
                className="max-w-full h-auto rounded-lg"
              />
            </CardContent>
          </Card>
        )}

        {/* Approval Actions */}
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle>Your Approval</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-600">
              Please review the above brochure details carefully. If everything looks correct, 
              click "Approve" to give us the go-ahead to publish. If you'd like any changes, 
              please describe them below.
            </p>

            <div>
              <Label className="mb-2 block">Request Amendments (optional)</Label>
              <Textarea
                placeholder="Describe any changes you'd like us to make..."
                value={amendments}
                onChange={(e) => setAmendments(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={handleApprove}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Approve Brochure
              </Button>
              <Button 
                onClick={handleRequestAmendments}
                disabled={submitting || !amendments.trim()}
                variant="outline"
                className="flex-1"
              >
                Request Amendments
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400">© Goodacres Estate Agents</p>
        </div>
      </footer>
    </div>
  );
};

export default SellerBrochureApprovalPage;
